import { ComponentType, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { findHelpEntry, findGuideEntry, HelpEntry, GuideEntry } from '../registry/helpRegistry';

// ─── טיפוסים ───────────────────────────────────────────────────────────────

type SupportedLang = 'he' | 'en';

interface UseHelpResult {
  Content: ComponentType | null;
  frontmatter: Record<string, unknown> | null;
  entry: HelpEntry | undefined;
  loading: boolean;
  notFound: boolean;
}

interface UseGuideResult {
  Content: ComponentType | null;
  frontmatter: Record<string, unknown> | null;
  entry: GuideEntry | undefined;
  loading: boolean;
  notFound: boolean;
}

// ─── hooks ─────────────────────────────────────────────────────────────────

/**
 * טוען עזרה הקשרית לפי helpKey.
 * בוחר שפה לפי i18n.language ונופל ל-Hebrew אם אין תוכן בשפה הנוכחית.
 * race-safe: אם helpKey משתנה לפני שה-import הסתיים — התוצאה נזרקת.
 */
export function useHelp(helpKey: string | null | undefined): UseHelpResult {
  const { i18n } = useTranslation();
  const [Content, setContent] = useState<ComponentType | null>(null);
  const [frontmatter, setFrontmatter] = useState<Record<string, unknown> | null>(null);
  const [loading, setLoading] = useState(false);
  const [notFound, setNotFound] = useState(false);

  const entry = helpKey ? findHelpEntry(helpKey) : undefined;

  useEffect(() => {
    if (!helpKey || !entry) {
      setContent(null);
      setFrontmatter(null);
      setLoading(false);
      setNotFound(!helpKey ? false : true);
      return;
    }

    let cancelled = false;
    const lang = resolveLang(i18n.language, entry.contentModule);

    setLoading(true);
    setContent(null);
    setFrontmatter(null);
    setNotFound(false);

    entry.contentModule[lang]!()
      .then((mod) => {
        if (cancelled) return;
        setContent(() => mod.default);
        setFrontmatter(mod.frontmatter as Record<string, unknown>);
      })
      .catch(() => {
        if (cancelled) return;
        setNotFound(true);
      })
      .finally(() => {
        if (cancelled) return;
        setLoading(false);
      });

    return () => { cancelled = true; };
  }, [helpKey, i18n.language, entry]);

  return { Content, frontmatter, entry, loading, notFound };
}

/**
 * טוען מדריך תפקיד לפי roleId.
 * fallback לעברית אם אין תוכן בשפה הנוכחית.
 * race-safe: אם roleId משתנה לפני שה-import הסתיים — התוצאה נזרקת.
 */
export function useGuide(roleId: number | null | undefined): UseGuideResult {
  const { i18n } = useTranslation();
  const [Content, setContent] = useState<ComponentType | null>(null);
  const [frontmatter, setFrontmatter] = useState<Record<string, unknown> | null>(null);
  const [loading, setLoading] = useState(false);
  const [notFound, setNotFound] = useState(false);

  const entry = roleId != null ? findGuideEntry(roleId) : undefined;

  useEffect(() => {
    if (roleId == null || !entry) {
      setContent(null);
      setFrontmatter(null);
      setLoading(false);
      setNotFound(roleId != null);
      return;
    }

    let cancelled = false;
    const lang = resolveLang(i18n.language, entry.contentModule);

    setLoading(true);
    setContent(null);
    setFrontmatter(null);
    setNotFound(false);

    entry.contentModule[lang]!()
      .then((mod) => {
        if (cancelled) return;
        setContent(() => mod.default);
        setFrontmatter(mod.frontmatter as Record<string, unknown>);
      })
      .catch(() => {
        if (cancelled) return;
        setNotFound(true);
      })
      .finally(() => {
        if (cancelled) return;
        setLoading(false);
      });

    return () => { cancelled = true; };
  }, [roleId, i18n.language, entry]);

  return { Content, frontmatter, entry, loading, notFound };
}

// ─── פונקציית עזר ─────────────────────────────────────────────────────────

function resolveLang(
  i18nLang: string,
  contentModule: { he: unknown; en?: unknown }
): SupportedLang {
  const preferred: SupportedLang = i18nLang === 'en' ? 'en' : 'he';
  if (preferred === 'en' && contentModule.en) return 'en';
  return 'he';
}
